if (!customElements.get('sprplx-restock')) {
  // Page-memory only: survive Shopify section replacement without persisting
  // email or consent in cookies/localStorage or carrying it to another product.
  const drafts = new Map();
  customElements.define('sprplx-restock', class extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('.product-details');
      if (!this.scope) return;
      this.section = this.closest('.shopify-section');
      this.key = `${this.section.id}:${this.dataset.productId}:${this.dataset.formId}`;
      this.draft = drafts.get(this.key) || { open: false };
      drafts.set(this.key, this.draft);
      this.pending = false;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.panel = this.querySelector('.sprplx-restock-panel');
      this.panel.id = `restock-panel-${this.dataset.productId}`;
      this.status = this.querySelector('[data-restock-status]');
      this.closeButton = this.querySelector('.sprplx-restock-close');
      this.scope.addEventListener('click', e => {
        if (this.closeButton.contains(e.target)) {
          e.preventDefault(); e.stopImmediatePropagation(); this.close(true); return;
        }
        const host = this.host();
        if (host?.contains(e.target) && (this.pending || !this.canNotify())) {
          e.preventDefault(); e.stopImmediatePropagation(); return;
        }
        const trigger = e.target.closest('[data-restock-trigger]');
        if (!trigger) return;
        e.preventDefault(); e.stopImmediatePropagation();
        if (this.pending || !this.canNotify()) return;
        this.open(true);
      }, { ...options, capture: true });
      this.scope.addEventListener('submit', e => {
        const isCart = e.target.matches('form[data-type="add-to-cart-form"]');
        const isRestock = this.host()?.contains(e.target);
        if ((isCart && (this.pending || this.dataset.available !== 'true')) ||
            (isRestock && (this.pending || !this.canNotify() || !this.draft.open))) {
          e.preventDefault(); e.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
      for (const event of ['input', 'change']) {
        this.scope.addEventListener(event, e => {
          if (e.target.closest('variant-picker')) this.updateHeading();
          if (!this.restoring && !this.pending && this.host()?.contains(e.target)) this.captureFields();
        }, { ...options, capture: true });
      }
      this.scope.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !this.panel.hidden) this.close(true);
      }, options);
      this.section.addEventListener('variant:selected', () => {
        this.captureFields();
        this.pending = true;
        this.updateHeading();
        this.setBusy(true);
        if (this.draft.open) this.status.textContent = this.status.dataset.loading;
      }, options);
      this.section.addEventListener('variant:update', e => {
        if (String(e.detail.data.productId) !== this.dataset.productId) return;
        this.pending = false;
        this.dataset.variantId = e.detail.resource?.id || '';
        this.dataset.available = String(e.detail.resource?.available === true);
        // Run after the theme has morphed the purchase controls and picker.
        queueMicrotask(() => {
          if (!this.isConnected) return;
          if (!this.canNotify()) this.close(false);
          else if (this.draft.open) this.open(false);
          this.decorate();
          this.setBusy(false);
        });
      }, options);
      this.observer = new MutationObserver(() => this.ready());
      this.observer.observe(this.scope, { childList: true, subtree: true });
      if (this.draft.open && this.canNotify()) this.open(false);
      else if (!this.canNotify()) this.close(false);
      this.decorate();
    }
    host() { return this.scope.querySelector(`#omnisend-embedded-v2-${this.dataset.formId}`); }
    canNotify() { return Boolean(this.dataset.variantId) && this.dataset.available === 'false'; }
    updateHeading() {
      const host = this.host();
      if (!host) return;
      if (!host.contains(this.closeButton)) host.append(this.closeButton);
      const heading = host.querySelector('form h3');
      if (!heading) return;
      const selected = this.dataset.defaultVariant === 'true' ? [] :
        [...this.scope.querySelectorAll('variant-picker input:checked, variant-picker select')]
          .map(input => input.value.trim()).filter(value => value && !/^default title$/i.test(value));
      const values = { product: this.dataset.productTitle, options: selected.length ? ` (${selected.join(' / ')})` : '' };
      const text = this.dataset.headingTemplate.replace(/\[(product|options)\]/g, (_, key) => values[key]);
      heading.classList.add('sprplx-restock-heading');
      if (heading.textContent !== text) heading.textContent = text;
    }
    captureFields() {
      const host = this.host();
      const input = host?.querySelector('input[type="email"]');
      const checkbox = host?.querySelector('input[name="gdpr"]');
      if (input) this.draft.email = input.value;
      if (checkbox) this.draft.marketing = checkbox.checked;
    }
    restoreFields(host) {
      const input = host?.querySelector('input[type="email"]');
      const checkbox = host?.querySelector('input[name="gdpr"]');
      this.restoring = true;
      // Native app re-renders replace the fields. Restore each replacement once,
      // using standard form events so the native validation/consent UI follows.
      if (input && input !== this.emailField) {
        this.emailField = input;
        if (this.draft.email !== undefined) {
          input.value = this.draft.email;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      if (checkbox && checkbox !== this.marketingField) {
        this.marketingField = checkbox;
        checkbox.required = false;
        checkbox.setAttribute('aria-required', 'false');
        if (this.draft.marketing !== undefined) {
          checkbox.checked = this.draft.marketing;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.restoring = false;
      return input;
    }
    setBusy(busy) {
      const host = this.host();
      if (!host) return;
      const form = host.querySelector('form');
      if (form) form.inert = busy || !this.canNotify();
      host.setAttribute('aria-busy', String(busy));
    }
    decorate() {
      const trigger = this.scope.querySelector('[data-restock-trigger]');
      if (trigger && this.canNotify() && !this.pending) trigger.disabled = false;
      trigger?.setAttribute('aria-controls', `${this.panel.id} omnisend-embedded-v2-${this.dataset.formId}`);
      trigger?.setAttribute('aria-expanded', String(!this.panel.hidden));
    }
    open(focus) {
      this.draft.open = true;
      this.panel.hidden = false;
      this.scope.setAttribute('data-restock-open', '');
      const url = new URL(location.href);
      url.searchParams.set('variant', this.dataset.variantId);
      history.replaceState(history.state, '', url);
      this.updateHeading();
      this.decorate();
      this.status.textContent = this.status.dataset.loading;
      this.focusPending = focus;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        if (!this.ready()) this.status.textContent = this.status.dataset.error;
      }, 15000);
      this.ready();
    }
    ready() {
      const host = this.host();
      this.updateHeading();
      this.setBusy(this.pending);
      if (this.panel.hidden || this.pending) return false;
      const input = this.restoreFields(host);
      const success = host?.querySelector(`.omnisend-form-${this.dataset.formId}-success-sections-container`);
      if (success?.getClientRects().length) {
        clearTimeout(this.timeout);
        if (this.status.textContent) this.status.textContent = '';
        this.focusPending = false;
        return true;
      }
      if (!input || !input.getClientRects().length) return false;
      clearTimeout(this.timeout);
      if (this.status.textContent) this.status.textContent = '';
      if (this.focusPending) { this.focusPending = false; input.focus({ preventScroll: true }); }
      return true;
    }
    close(focus) {
      this.captureFields();
      this.draft.open = false;
      this.panel.hidden = true;
      this.scope.removeAttribute('data-restock-open');
      const trigger = this.scope.querySelector('[data-restock-trigger]');
      trigger?.setAttribute('aria-expanded', 'false');
      clearTimeout(this.timeout);
      if (focus) trigger?.focus();
    }
    disconnectedCallback() {
      // Keep open intent and already captured fields for the replacement section.
      // Do not read a partially removed app block or treat removal as user close.
      this.abort?.abort(); this.observer?.disconnect(); clearTimeout(this.timeout);
      if (this.closeButton) this.panel?.append(this.closeButton);
    }
  });
}
